import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth.store';
import { useWebSocket } from './useWebSocket';
import type { PermissionChangeEvent } from '../services/websocket/websocket.service';

// Variable global para debounce de refresh entre todas las instancias del hook
let globalLastRefreshTime = 0;
const REFRESH_DEBOUNCE_MS = 3000;

export const usePermissions = () => {
  const user = useAuthStore((state) => state.user);
  const loadUser = useAuthStore((state) => state.loadUser);
  const { addEventListener, removeEventListener, isConnected } = useWebSocket();
  const [permissionChangeNotification, setPermissionChangeNotification] = useState<PermissionChangeEvent | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isRefreshingRef = useRef(false);

  // Verificar si el usuario tiene la estructura completa con roles
  const getCompleteUser = () => {
    
    if (!user) {
      return null;
    }
    
    // Si ya tiene la estructura completa (roles con módulos), usarla directamente
    if ('roles' in user && Array.isArray(user.roles)) {
      return user as any;
    }
    
    // Si tiene la estructura básica, retornar null (no hay permisos detallados)
    return null;
  };

  const completeUser = getCompleteUser();

  // Verificar si el usuario tiene un permiso específico en un módulo
  const hasPermission = useCallback((moduleName: string, permissionName: string): boolean => {
    
    if (!completeUser?.roles) {
      return false;
    }

    const hasPermissionResult = completeUser.roles.some((role: any) => 
      role.modulos.some((module: any) => {
        if (module.nombre === moduleName) {
          return module.permisos.some((permission: any) => {
            const match = permission.nombre === permissionName;
            return match;
          });
        }
        return false;
      })
    );
    
    return hasPermissionResult;
  }, [completeUser]);

  // Verificar si el usuario tiene acceso a un módulo (cualquier permiso)
  const hasModuleAccess = useCallback((moduleName: string): boolean => {
    if (!completeUser?.roles) return false;

    return completeUser.roles.some((role: any) => 
      role.modulos.some((module: any) => 
        module.nombre === moduleName && module.permisos.length > 0
      )
    );
  }, [completeUser]);

  // Obtener todos los permisos del usuario para un módulo específico
  const getModulePermissions = useCallback((moduleName: string): string[] => {
    if (!completeUser?.roles) return [];

    const permissions: string[] = [];
    
    completeUser.roles.forEach((role: any) => {
      role.modulos.forEach((module: any) => {
        if (module.nombre === moduleName) {
          module.permisos.forEach((permission: any) => {
            if (!permissions.includes(permission.nombre)) {
              permissions.push(permission.nombre);
            }
          });
        }
      });
    });

    return permissions;
  }, [completeUser]);

  // Verificar múltiples permisos (ANY - al menos uno)
  const hasAnyPermission = useCallback((moduleName: string, permissionNames: string[]): boolean => {
    return permissionNames.some(permission => hasPermission(moduleName, permission));
  }, [hasPermission]);

  // Verificar múltiples permisos (ALL - todos)
  const hasAllPermissions = useCallback((moduleName: string, permissionNames: string[]): boolean => {
    return permissionNames.every(permission => hasPermission(moduleName, permission));
  }, [hasPermission]);

  // Verificar si es administrador
  const isAdmin = useCallback((): boolean => {
    return completeUser?.tipoUsuario === 'administrador';
  }, [completeUser]);

  // Mapeo de rutas a nombres de módulos
  const getModuleNameFromRoute = (pathname: string): string | null => {
    // Normalizar la ruta removiendo el prefijo /dashboard si existe
    const cleanPath = pathname.replace('/dashboard', '');
    
    if (cleanPath === '/' || cleanPath === '') return 'Dashboard';
    
    // Rutas específicas primero (más específicas antes que las generales)
    if (cleanPath.startsWith('/miembros/usuarios')) return 'Usuarios';
    if (cleanPath.startsWith('/miembros/membresias')) return 'Membresías';
    if (cleanPath.startsWith('/seguridad/roles')) return 'Roles';
    if (cleanPath.startsWith('/seguridad/modules')) return 'Módulos';
    if (cleanPath.startsWith('/seguridad/permissions')) return 'Permisos';
    if (cleanPath.startsWith('/iglesia/ministerios')) return 'Ministerios';
    if (cleanPath.startsWith('/iglesia/contactos')) return 'Contactos';
    if (cleanPath.startsWith('/iglesia/chat')) return 'Mensajes';
    if (cleanPath.startsWith('/miembro')) return 'Mi Perfil';
    if (cleanPath.startsWith('/mayordomia')) return 'Mayordomía';
    if (cleanPath.startsWith('/config/contenido-publico')) return 'Contenido Público';
    
    // Rutas generales después
    if (cleanPath.startsWith('/usuarios')) return 'Usuarios';
    if (cleanPath.startsWith('/seguridad')) return 'Seguridad';
    if (cleanPath.startsWith('/config')) return 'Configuración';
    if (cleanPath.startsWith('/gestion-web')) return 'Gestión Web';
    if (cleanPath.startsWith('/iglesia')) return 'Iglesia';
    
    return null;
  };
  
  // Verificar si el usuario puede acceder a la ruta actual
  const checkCurrentRouteAccess = useCallback(() => {
    // No evaluar mientras la sesión aún se está hidratando (usuario null en el store):
    // hasPermission devolvería false y provocaría una redirección falsa al dashboard.
    if (!completeUser) return;

    const moduleName = getModuleNameFromRoute(location.pathname);
    if (moduleName && moduleName !== 'Dashboard') {
      const hasAccess = hasPermission(moduleName, 'ver');
      if (!hasAccess) {
        console.log(`🚫 Access denied to ${moduleName}, redirecting to dashboard`);
        navigate('/dashboard');
      }
    }
  }, [location.pathname, hasPermission, navigate]);

  // Manejar cambios de permisos en tiempo real via WebSocket
  useEffect(() => {
    if (!isConnected || !user?.id) return;

    const handlePermissionChange = async (event: PermissionChangeEvent) => {
      // Verificar si el evento afecta al usuario actual
      // ROLE_UPDATED: afecta si es dirigido al usuario o si tiene el rol
      // MODULE_UPDATED: afecta a todos los usuarios (los módulos son globales)
      const isAffected = event.type === 'MODULE_UPDATED' ||
                        event.userId === user.id || 
                        (user as any)?.roles?.some((role: any) => role.id === event.roleId);

      if (!isAffected) return;

      // Mostrar notificación SIEMPRE (una por instancia del hook está bien)
      setPermissionChangeNotification(event);

      // Proteger contra múltiples refresh simultáneos (globales e instancia)
      const now = Date.now();
      if (isRefreshingRef.current || (now - globalLastRefreshTime) < REFRESH_DEBOUNCE_MS) {
        console.log('⏭️ Skipping permission refresh (debounce or already refreshing)');
        return;
      }

      isRefreshingRef.current = true;
      globalLastRefreshTime = now;

      try {
        console.log('🔄 Refreshing user permissions due to WebSocket event:', event);
        await loadUser();
        console.log('✅ User permissions refreshed successfully');
        
        // Verificar si el usuario aún tiene acceso a la ruta actual
        setTimeout(() => {
          checkCurrentRouteAccess();
        }, 100);

      } catch (error) {
        console.error('❌ Error refreshing user permissions:', error);
      } finally {
        isRefreshingRef.current = false;
      }
    };

    // Agregar listener para cambios de permisos
    addEventListener('permission_change', handlePermissionChange);

    // Cleanup
    return () => {
      removeEventListener('permission_change', handlePermissionChange);
    };
  }, [isConnected, user?.id, loadUser, addEventListener, removeEventListener, checkCurrentRouteAccess]);

  // Verificar acceso a la ruta actual cuando cambien los permisos o la ruta
  useEffect(() => {
    if (completeUser) {
      checkCurrentRouteAccess();
    }
  }, [completeUser, checkCurrentRouteAccess]);

  // Función para limpiar notificación de cambio
  const clearPermissionChangeNotification = () => {
    setPermissionChangeNotification(null);
  };

  return {
    hasPermission,
    hasModuleAccess,
    getModulePermissions,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    user: completeUser,
    // Nuevas propiedades relacionadas con WebSocket
    permissionChangeNotification,
    clearPermissionChangeNotification,
    isWebSocketConnected: isConnected
  };
};
